<template>
  <div
    :id="id"
    ref="element"
    class="element image draggable"
    :class="[
      cssClasses,
      animationInStyle,
      animationOutStyle,
      { selected: isSelected, locked },
    ]"
    :style="[basicStyles, transformStyles]"
  >
    <transition
      :enter-active-class="'animated ' + animationIn"
      :leave-active-class="'animated ' + animationOut"
      :duration="{ leave: leaveDuration }"
      mode=""
      @after-enter="afterEnter"
      @before-leave="beforeLeave"
    >
      <div
        v-show="toggle"
        class="image-wrap"
        :class="[fillTypeClass, maskClass, loopingClass]"
        :style="[
          animationStyles,
          borderStyles,
          imageBorderRadiusStyles,
          boxShadowStyles,
          blurStyles,
          basicStyles,
        ]"
      >
        <img
          v-show="imageUrl"
          :src="imageUrl"
          :data-test-image-optimized="imageMetaOptimizeApplied"
          @error="handleImageError"
        />
      </div>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ElementsMixins from '../../mixins/elements-mixins';
import MoveMixins from '../../mixins/move-mixins';
import ResizeMixins from '../../mixins/resize-mixins';
import { buildImageUrl } from '../../utils/useMedia';

export default {
  name: 'ImageElement',
  mixins: [ElementsMixins, ResizeMixins, MoveMixins],
  data() {
    return {
      useFallback: false,
      observer: null,
    };
  },
  computed: {
    ...mapGetters('graphicsTemplate', [
      'getElementValueByProp',
      'getDataValue',
    ]),
    fillType() {
      return this.getElementValueByProp(this.id, 'fillType');
    },
    mask() {
      return this.getElementValueByProp(this.id, 'mask');
    },
    maskShape() {
      return this.getElementValueByProp(this.id, 'maskShape');
    },
    maskPosition() {
      return this.getElementValueByProp(this.id, 'maskPosition') ?? 'center-center';
    },
    borderTopLeftRadius() {
      return this.getElementValueByProp(this.id, 'borderTopLeftRadius');
    },
    borderTopRightRadius() {
      return this.getElementValueByProp(this.id, 'borderTopRightRadius');
    },
    borderBottomRightRadius() {
      return this.getElementValueByProp(this.id, 'borderBottomRightRadius');
    },
    borderBottomLeftRadius() {
      return this.getElementValueByProp(this.id, 'borderBottomLeftRadius');
    },
    mappedImage() {
      return this.isMapped('imageUrl') && this.getDataValue(this.id, 'imageUrl');
    },
    imageUrl: {
      get() {
        if (!this.useFallback && this.mappedImage) {
          return this.mappedImage;
        }

        // use "fallback" if there is an error
        const imageUrl = this.getElementValueByProp(this.id, 'imageUrl');
        if (!imageUrl) {
          return imageUrl;
        }

        const imageMeta = this.getElementValueByProp(this.id, 'imageMeta');

        return buildImageUrl(imageUrl, imageMeta);
      },
      set(v) {
        this.updateOneElementValue('imageUrl', v);
      },
    },
    fillTypeClass() {
      return this.fillType ? this.fillType : '';
    },
    maskClass() {
      return this.mask ? `mask ${this.maskShape} ${this.maskPosition}` : '';
    },
    imageBorderRadiusStyles() {
      return this.mask && this.maskShape === 'rectangle'
        ? {
          borderRadius: `${this.borderTopLeftRadius}px ${this.borderTopRightRadius}px ${this.borderBottomRightRadius}px ${this.borderBottomLeftRadius}px`,
        }
        : '';
    },
  },
  watch: {
    mappedImage() {
      this.useFallback = false;
    },
  },
  mounted() {
    const { element } = this.$refs;
    // MutationObserver to detect when draggable tries to change the style
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'style' && element.style.transform !== this.transformStyles.transform) {
          // Set the transform style back to what it should be since draggable changes it
          // outside of the Vue instance
          element.style.transform = this.transformStyles.transform;
        }
      });
    });

    // Start observing the element
    this.observer.observe(element, {
      attributes: true, // Watch for attribute changes
      attributeFilter: ['style'], // Specifically watch the 'style' attribute
    });
  },
  beforeUnmount() {
    // Disconnect the observer when the component is destroyed
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    handleImageError() {
      this.useFallback = true;
    },
  },
};
</script>
